import { Flex, Icon, IconButton, Select, Text } from '@chakra-ui/react'
import { faChevronDoubleLeft, faChevronDoubleRight, faChevronLeft, faChevronRight } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { Table } from '@tanstack/react-table'

export interface EditableGridPaginationProps<T> {
    table: Table<T>
    totalCount: number
    pageSizeOptions?: number[]
    showPageSizeSelector?: boolean
}

export function EditableGridPagination<T>({
    table,
    totalCount,
    pageSizeOptions = [10, 20, 50, 100, -1],
    showPageSizeSelector = true
}: EditableGridPaginationProps<T>) {
    const {
        getState,
        setPageIndex,
        setPageSize,
        getPageCount,
        getCanPreviousPage,
        getCanNextPage,
        previousPage,
        nextPage,
    } = table

    const { pagination } = getState()
    const { pageIndex, pageSize } = pagination

    const pageCount = getPageCount()
    const canPreviousPage = getCanPreviousPage()
    const canNextPage = getCanNextPage()

    // Determine the value to display in the select; default to -1 if it's our "All" marker (max integer)
    const selectValue = pageSize === Number.MAX_SAFE_INTEGER ? -1 : pageSize

    return (
        <Flex justifyContent="space-between" alignItems="center" p={2} gap={4}>
            {showPageSizeSelector && (
                <Flex alignItems="center" gap={2}>
                    <Text fontSize="sm">Záznamů na stránku</Text>
                    <Select
                        size="sm"
                        w="auto"
                        variant='outline'
                        value={selectValue}
                        onChange={e => {
                            const val = Number(e.target.value)
                            setPageSize(val === -1 ? Number.MAX_SAFE_INTEGER : val)
                        }}
                    >
                        {pageSizeOptions.map(opt => (
                            <option key={opt} value={opt}>
                                {opt === -1 ? 'Vše' : opt}
                            </option>
                        ))}
                    </Select>
                </Flex>
            )}

            <Flex gap={1}>
                <IconButton
                    icon={<Icon as={FontAwesomeIcon} icon={faChevronDoubleLeft} />}
                    onClick={() => setPageIndex(0)}
                    isDisabled={!canPreviousPage}
                    variant="ghost"
                    aria-label='First page'
                    p={2}
                />
                <IconButton
                    icon={<Icon as={FontAwesomeIcon} icon={faChevronLeft} />}
                    onClick={() => previousPage()}
                    isDisabled={!canPreviousPage}
                    variant="ghost"
                    aria-label='Previous page'
                    p={2}
                />
                <IconButton
                    icon={<Icon as={FontAwesomeIcon} icon={faChevronRight} />}
                    onClick={() => nextPage()}
                    isDisabled={!canNextPage}
                    variant="ghost"
                    aria-label='Next page'
                    p={2}
                />
                <IconButton
                    icon={<Icon as={FontAwesomeIcon} icon={faChevronDoubleRight} />}
                    onClick={() => setPageIndex(pageCount - 1)}
                    isDisabled={!canNextPage}
                    variant="ghost"
                    aria-label='Last page'
                    p={2}
                />
            </Flex>

            <Text fontSize="sm">
                Stránka {pageIndex + 1} z {pageCount} ({totalCount} celkem)
            </Text>
        </Flex>
    )
}
